import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ClientDecisionCard } from './ClientDecisionCard';
import { decisionForCell, type DecisionOutcome, type DecisionRecord } from '../../utils/decisionGrid';
import { getCellDecision, saveCellDecision } from '../../utils/discoveryGridApi';

/**
 * GridDecisionPanel — the decision card in the Discovery Grid cell drawer (RESTORE_PLAN R2.7).
 * When a cell's answer is an architecture choice that departs from best practice (more service
 * desks, request types or workflow statuses than recommended; SLAs after go-live), the client
 * decides here and the consultant sees what was decided; a review request reaches the
 * consultant's Queue (Decisions). A recommended answer, or any other question, shows nothing.
 */

interface GridDecisionPanelProps {
  companyId: string;
  cell: { id: string; kind?: string; text?: string; answer?: unknown };
  accessToken: string;
  viewerRole: 'consultant' | 'client';
  viewer?: { id?: string; name?: string };
  /** Opens the answer for editing (after the client chooses the recommended approach). */
  onEditAnswer?: () => void;
}

export function GridDecisionPanel({ companyId, cell, accessToken, viewerRole, viewer, onEditAnswer }: GridDecisionPanelProps) {
  const decision = useMemo(() => decisionForCell(cell, viewer?.id ?? ''), [cell, viewer?.id]);
  const [record, setRecord] = useState<DecisionRecord | null>(null);
  const [busy, setBusy] = useState(false);
  const show = !!decision && !decision.evaluation.isRecommended;

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    getCellDecision(companyId, cell.id, accessToken)
      .then((r) => { if (!cancelled) setRecord(r); })
      .catch(() => { /* the card works without the history */ });
    return () => { cancelled = true; };
  }, [show, companyId, cell.id, accessToken]);

  if (!show || !decision) return null;
  // A decision recorded for another answer no longer applies once the answer changes.
  const current = record && record.selectedOption === decision.optionId ? record : null;

  const save = async (outcome: DecisionOutcome, reason?: string) => {
    setBusy(true);
    try {
      const saved = await saveCellDecision(companyId, { cellId: cell.id, decisionNodeId: decision.node.id, selectedOption: decision.optionId, outcome, reason }, accessToken);
      setRecord(saved);
      if (outcome === 'request_review') toast.success('Your consultant will review this choice');
      else if (outcome === 'proceed') toast.success('Noted: you are going ahead with this approach');
      else {
        toast.success(`Noted: you will follow the recommendation. Update your answer to match.`);
        onEditAnswer?.();
      }
    } catch (e: any) {
      toast.error(e?.message || 'Could not save your decision');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4" data-testid="grid-decision">
      <ClientDecisionCard
        decisionNode={decision.node}
        evaluation={decision.evaluation}
        status={current}
        readOnly={viewerRole === 'consultant'}
        loading={busy}
        userId={viewer?.id}
        accessToken={accessToken}
        context={{ answer: cell.answer }}
        onAcceptRecommendation={() => void save('accept_recommendation')}
        onRequestReview={(reason) => void save('request_review', reason)}
        onProceedAnyway={decision.evaluation.severity === 'block' ? undefined : (reason) => void save('proceed', reason)}
      />
    </div>
  );
}
